import type { TeamWith } from "@redotech/account-common/base-team";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { PickSessionItem } from "@redotech/redo-fulfillment-wms-common/schema/pick-session";
import { StorageLocationType } from "@redotech/redo-fulfillment-wms-common/schema/storage-location";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { LocationId, type InventoryItemId } from "@redotech/redo-inventory-id";
import { ProductId } from "@redotech/redo-product-id";
import {
  InventoryLevelId,
  StorageLocationId,
} from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import {
  Fragment,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { InventoryItemIds } from "../shared/inventory-item-ids";
import { LotBadge } from "../shared/lot-badge";
import { QuantityIncrementDecrement } from "../shared/quantity-increment-decrement";
import {
  getProblemPickInventoryIdentity,
  type ProblemPickSource,
} from "./problem-pick-source-allocation";
import * as styles from "./tote-scanning-page.module.css";
import { usePickTitle } from "./use-pick-title";
import { isItemMatch, playErrorSound, playSuccessSound } from "./util";

interface BinOption {
  inventoryLevelId: InventoryLevelId;
  storageLocationId: StorageLocationId;
  inventoryItemId?: InventoryItemId | null;
  binName: string;
  binType?: string | null;
  availableQuantity: number;
  lastCountedAt?: Date | null;
  lotNumber?: string | null;
  expirationDate?: Date | null;
}

const RECEIVE_SENTINEL_ID = "external";

interface BinQuantityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (quantity: number, sources?: ProblemPickSource[]) => void;
  onConfirmLoad: LoadState<void>;
  item: PickSessionItem | FulfillmentOrderLineItem;
  quantityLeftToPick: number;
  team?: TeamWith<{ outboundLabels: OutboundLabelSettings }> | null;
  scanValue: string | null;
  resetScanValue: () => void;
  requireSourceBin?: boolean;
  locationId?: LocationId;
  productId?: ProductId;
  requiredInventoryItemId?: string | null;
  confirmTotal?: boolean;
  /** Opens with this bin's stock added as the source. */
  initialStorageLocationId?: StorageLocationId | null;
}

const SourceRow = memo(function SourceRow({
  title,
  subtitle,
  value,
  max,
  onChange,
  onRemove,
}: {
  title: ReactNode;
  subtitle: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Flex align="center" dir="row" gap="md" px="lg" py="md" w="full">
      <Flex dir="column" gap="xxs" grow={1}>
        {title}
        <Text fontSize="xs" textColor="tertiary">
          {subtitle}
        </Text>
      </Flex>
      <Flex shrink={0}>
        <QuantityIncrementDecrement
          compact
          max={max}
          min={0}
          setValue={onChange}
          size="sm"
          value={value}
        />
      </Flex>
      <RedoButton
        aria-label={t("Remove source")}
        hierarchy="tertiary"
        IconLeading={XIcon}
        onClick={onRemove}
        size="xs"
      />
    </Flex>
  );
});

export const BinQuantityModal = memo(function BinQuantityModal({
  isOpen,
  onClose,
  onConfirm,
  item,
  quantityLeftToPick,
  team,
  scanValue,
  onConfirmLoad,
  resetScanValue,
  requireSourceBin,
  locationId,
  productId,
  requiredInventoryItemId,
  confirmTotal = false,
  initialStorageLocationId,
}: BinQuantityModalProps) {
  const { t } = useTranslation();
  const getTitle = usePickTitle();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [selectedQuantity, setSelectedQuantity] = useState(
    confirmTotal ? quantityLeftToPick : Math.min(1, quantityLeftToPick),
  );
  const screenType = useScreenType();
  const isMobile = screenType === ScreenType.MOBILE;
  const inventoryItemId =
    "quantityPicked" in item ? item.inventoryItemId : item.imsInventoryItemId;

  const [allocations, setAllocations] = useState<Map<string, number>>(
    new Map(),
  );
  const [visibleBinIds, setVisibleBinIds] = useState<Set<string>>(new Set());

  const binsLoad = useLoad(
    async (signal) => {
      if (!requireSourceBin || !locationId || !productId) {
        return undefined;
      }
      const result = await rpcClient.getBinsForProduct(
        { locationId, productId, includeStorage: true, includeReceiving: true },
        { signal },
      );
      return result.bins;
    },
    [rpcClient, requireSourceBin, locationId, productId],
  );

  const appliedInitialBinRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setAllocations(new Map());
      setVisibleBinIds(new Set());
      appliedInitialBinRef.current = false;
    }
  }, [isOpen]);

  // Receiving stock hasn't been put away yet, so auto-allocation takes it last.
  const countedBins: BinOption[] = useMemo(
    () =>
      (binsLoad.value ?? [])
        .filter((bin) => bin.lastCountedAt != null)
        .sort(
          (a, b) =>
            Number(a.binType === StorageLocationType.RECEIVING) -
            Number(b.binType === StorageLocationType.RECEIVING),
        ),
    [binsLoad.value],
  );

  const availableBinOptions: BinOption[] = useMemo(() => {
    if (!requiredInventoryItemId) {
      return countedBins;
    }
    return countedBins.filter(
      (bin) => bin.inventoryItemId === requiredInventoryItemId,
    );
  }, [countedBins, requiredInventoryItemId]);

  const visibleBins = availableBinOptions.filter((bin) =>
    visibleBinIds.has(bin.inventoryLevelId),
  );

  useEffect(() => {
    if (
      !isOpen ||
      !initialStorageLocationId ||
      !binsLoad.value ||
      appliedInitialBinRef.current
    ) {
      return;
    }
    appliedInitialBinRef.current = true;
    const initial = new Map<string, number>();
    let remaining = quantityLeftToPick;
    for (const bin of availableBinOptions) {
      if (remaining <= 0) {
        break;
      }
      if (bin.storageLocationId !== initialStorageLocationId) {
        continue;
      }
      const quantity = Math.min(remaining, bin.availableQuantity);
      if (quantity > 0) {
        initial.set(bin.inventoryLevelId, quantity);
        remaining -= quantity;
      }
    }
    setAllocations(initial);
    setVisibleBinIds(new Set(initial.keys()));
  }, [
    isOpen,
    initialStorageLocationId,
    binsLoad.value,
    availableBinOptions,
    quantityLeftToPick,
  ]);

  const emptyBinsMessage =
    (binsLoad.value ?? []).length === 0
      ? t("No bins with available inventory")
      : countedBins.length === 0
        ? t("Bins holding this product have not been counted yet")
        : t("No counted bin holds the required inventory item");

  const totalAllocated = useMemo(
    () => [...allocations.values()].reduce((s, n) => s + n, 0),
    [allocations],
  );

  const updateAllocation = useHandler((binId: string, qty: number) => {
    setAllocations((prev) => {
      const next = new Map(prev);
      if (qty <= 0) {
        next.delete(binId);
      } else {
        next.set(binId, qty);
      }
      return next;
    });
  });

  const capacityFor = (binId: string): number =>
    binId === RECEIVE_SENTINEL_ID
      ? quantityLeftToPick
      : Math.min(
          quantityLeftToPick,
          availableBinOptions.find((bin) => bin.inventoryLevelId === binId)
            ?.availableQuantity ?? 0,
        );

  /** Adds a source row, prefilled with whatever is still unallocated. */
  const showBin = useHandler((binId: string) => {
    setVisibleBinIds((prev) => {
      const next = new Set(prev);
      next.add(binId);
      return next;
    });
    const unallocated = quantityLeftToPick - totalAllocated;
    const prefill = Math.min(unallocated, capacityFor(binId));
    if (prefill > 0 && !allocations.has(binId)) {
      updateAllocation(binId, prefill);
    }
  });

  const hideBin = useHandler((binId: string) => {
    setVisibleBinIds((prev) => {
      const next = new Set(prev);
      next.delete(binId);
      return next;
    });
    setAllocations((prev) => {
      const next = new Map(prev);
      next.delete(binId);
      return next;
    });
  });

  const buildSources = (): ProblemPickSource[] => {
    return [...allocations.entries()]
      .filter(([, qty]) => qty > 0)
      .map(([allocationId, quantity]) => {
        if (allocationId === RECEIVE_SENTINEL_ID) {
          return { type: "external" as const, quantity };
        }
        const bin = availableBinOptions.find(
          (option) => option.inventoryLevelId === allocationId,
        );
        if (!bin) {
          throw new Error("Selected inventory level is no longer available");
        }
        return {
          type: "inventoryLevel" as const,
          inventoryLevelId: bin.inventoryLevelId,
          quantity,
        };
      });
  };

  const handleBarcodeScanned = useHandler(async (scannedValue: string) => {
    if (!isItemMatch(item, scannedValue, team)) {
      toast(`Item does not match scanned value: ${scannedValue}`, {
        variant: "error",
      });
      void playErrorSound();
      resetScanValue();
      return;
    }

    if (requireSourceBin) {
      if (totalAllocated >= quantityLeftToPick) {
        toast(`Cannot pick more than ${quantityLeftToPick} items`, {
          variant: "error",
        });
        void playErrorSound();
        resetScanValue();
        return;
      }
      const hasRoom = (bin: BinOption) =>
        (allocations.get(bin.inventoryLevelId) ?? 0) < bin.availableQuantity;
      // Prefer a bin the user already added, then any bin with stock. Never
      // fall back to receiving new stock; that stays an explicit choice.
      const targetId = (
        visibleBins.find(hasRoom) ?? availableBinOptions.find(hasRoom)
      )?.inventoryLevelId;
      if (!targetId) {
        toast(
          t(
            "No bin has stock for another unit. Add a bin or receive it as new stock.",
          ),
          { variant: "error" },
        );
        void playErrorSound();
        resetScanValue();
        return;
      }
      setVisibleBinIds((prev) => new Set(prev).add(targetId));
      updateAllocation(targetId, (allocations.get(targetId) ?? 0) + 1);
      void playSuccessSound();
      resetScanValue();
      return;
    }

    if (confirmTotal && selectedQuantity >= quantityLeftToPick) {
      void playSuccessSound();
      onConfirm(selectedQuantity);
    } else if (selectedQuantity >= quantityLeftToPick) {
      toast(`Cannot pick more than ${quantityLeftToPick} items`, {
        variant: "error",
      });
      void playErrorSound();
    } else {
      const newQuantity = Math.min(selectedQuantity + 1, quantityLeftToPick);
      if (newQuantity === quantityLeftToPick) {
        void playSuccessSound();
        onConfirm(newQuantity);
      } else {
        setSelectedQuantity(newQuantity);
        void playSuccessSound();
      }
    }
    resetScanValue();
  });

  useEffect(() => {
    if (scanValue) {
      void handleBarcodeScanned(scanValue);
    }
  }, [scanValue, handleBarcodeScanned]);

  const confirmDisabled = requireSourceBin
    ? totalAllocated <= 0 || totalAllocated > quantityLeftToPick
    : selectedQuantity <= 0;

  if (isOpen && requireSourceBin && (binsLoad.pending || !binsLoad.value)) {
    return (
      <RedoBaseModal
        isOpen={isOpen}
        onModalCloseRequested={onClose}
        otherClasses={[styles.modalContentCompact]}
      >
        <Flex
          align="center"
          dir="column"
          gap="md"
          justify="center"
          p="3xl"
          w="full"
        >
          <RedoSpinner />
          <Text fontSize="sm" textColor="secondary">
            {t("Loading bins...")}
          </Text>
        </Flex>
      </RedoBaseModal>
    );
  }

  return (
    <RedoBaseModal
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      otherClasses={[styles.modalContentCompact]}
    >
      <Flex dir="column" gap="none" p={isMobile ? "xl" : "3xl"} w="full">
        <Flex align="center" justify="space-between" w="full">
          <Flex dir="column" gap="xs">
            <Text fontSize={isMobile ? "md" : "lg"} fontWeight="semibold">
              {confirmTotal
                ? t("Confirm total quantity ({{selected}}/{{total}})", {
                    selected: requireSourceBin
                      ? totalAllocated
                      : selectedQuantity,
                    total: quantityLeftToPick,
                  })
                : t("Confirm quantity ({{selected}}/{{total}})", {
                    selected: requireSourceBin
                      ? totalAllocated
                      : selectedQuantity,
                    total: quantityLeftToPick,
                  })}
            </Text>
            {!isMobile && (
              <Text fontSize="sm" textColor="secondary">
                {confirmTotal
                  ? t(
                      "Confirm you picked all {{total}}, or adjust the quantity.",
                      { total: quantityLeftToPick },
                    )
                  : t("Scan each item to pick, or adjust quantity manually.")}
              </Text>
            )}
          </Flex>
          <RedoButton
            aria-label="Close"
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size={isMobile ? "md" : "xl"}
          />
        </Flex>

        <Flex align="center" gap="md" py={isMobile ? "sm" : "3xl"} w="full">
          {item.imageUrl ? (
            <img
              alt={`Product ${item.sku}`}
              className={classNames(
                styles.productImage,
                isMobile && styles.productImageCompact,
              )}
              src={item.imageUrl}
            />
          ) : (
            <div
              className={classNames(
                styles.productImagePlaceholder,
                isMobile && styles.productImagePlaceholderCompact,
              )}
            />
          )}
          <Flex dir="column" gap="xxs" grow="1">
            {"bin" in item && item.bin && (
              <Text fontSize={isMobile ? "md" : "lg"} fontWeight="bold">
                {t("Bin:")} {item.bin}
              </Text>
            )}
            <Text fontSize={isMobile ? "xs" : "sm"} textColor="secondary">
              {getTitle(item)}
            </Text>
            {item.sku && (
              <Text fontSize="xs" textColor="secondary">
                {t("SKU:")} {item.sku}
              </Text>
            )}
            <InventoryItemIds inventoryItemIds={[inventoryItemId]} size="xs" />
            {item.customAttributes.find((ca) =>
              ca.key.toLowerCase().includes("brand style"),
            ) && (
              <Text fontSize="xs" textColor="secondary">
                {t("Brand style:")}{" "}
                {
                  item.customAttributes.find((ca) =>
                    ca.key.toLowerCase().includes("brand style"),
                  )?.value
                }
              </Text>
            )}
          </Flex>
        </Flex>

        <Divider classes={styles.lightDivider} />

        {requireSourceBin ? (
          <Flex dir="column" gap="md" py="lg" w="full">
            <Flex align="center" dir="row" justify="space-between">
              <Flex dir="column" gap="xxs">
                <Text fontSize="sm" fontWeight="semibold">
                  {t("Pick from")}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {t("Choose which bins the items came from.")}
                </Text>
              </Flex>
              <RedoBadge
                color={
                  totalAllocated === quantityLeftToPick ? "success" : "warning"
                }
                size="sm"
                text={t("{{allocated}} of {{total}}", {
                  allocated: totalAllocated,
                  total: quantityLeftToPick,
                })}
              />
            </Flex>
            {binsLoad.pending ? (
              <Flex align="center" justify="center" p="xl">
                <RedoSpinner />
              </Flex>
            ) : (
              <>
                {availableBinOptions.length === 0 && (
                  <Flex bgColor="secondary" borderRadius="md" p="md">
                    <Text fontSize="xs" textColor="secondary">
                      {emptyBinsMessage}
                    </Text>
                  </Flex>
                )}
                {(visibleBins.length > 0 ||
                  visibleBinIds.has(RECEIVE_SENTINEL_ID)) && (
                  <Flex
                    borderColor="secondary"
                    borderRadius="md"
                    borderStyle="solid"
                    borderWidth="1px"
                    dir="column"
                    gap="none"
                    w="full"
                  >
                    {visibleBins.map((bin, index) => (
                      <Fragment key={bin.inventoryLevelId}>
                        {index > 0 && <Divider classes={styles.lightDivider} />}
                        <SourceRow
                          max={Math.min(
                            quantityLeftToPick,
                            bin.availableQuantity,
                          )}
                          onChange={(v) =>
                            updateAllocation(bin.inventoryLevelId, v)
                          }
                          onRemove={() => hideBin(bin.inventoryLevelId)}
                          subtitle={[
                            t("{{count}} available", {
                              count: bin.availableQuantity,
                            }),
                            getProblemPickInventoryIdentity(
                              bin.inventoryItemId,
                            ),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          title={
                            <Flex align="center" dir="row" gap="xs">
                              <Text fontSize="sm" fontWeight="medium">
                                {bin.binName}
                              </Text>
                              {bin.binType ===
                                StorageLocationType.RECEIVING && (
                                <RedoBadge
                                  color="blueLight"
                                  size="xs"
                                  text={t("Receiving")}
                                />
                              )}
                              {bin.binType === StorageLocationType.STORAGE && (
                                <RedoBadge
                                  color="gray"
                                  size="xs"
                                  text={t("Storage")}
                                />
                              )}
                              {bin.lotNumber && (
                                <LotBadge
                                  expirationDate={bin.expirationDate}
                                  lotNumber={bin.lotNumber}
                                />
                              )}
                            </Flex>
                          }
                          value={allocations.get(bin.inventoryLevelId) ?? 0}
                        />
                      </Fragment>
                    ))}
                    {visibleBinIds.has(RECEIVE_SENTINEL_ID) &&
                      visibleBins.length > 0 && (
                        <Divider classes={styles.lightDivider} />
                      )}
                    {visibleBinIds.has(RECEIVE_SENTINEL_ID) && (
                      <SourceRow
                        max={quantityLeftToPick}
                        onChange={(v) =>
                          updateAllocation(RECEIVE_SENTINEL_ID, v)
                        }
                        onRemove={() => hideBin(RECEIVE_SENTINEL_ID)}
                        subtitle={t("Adds stock that isn't in any bin yet")}
                        title={
                          <Text fontSize="sm" fontWeight="medium">
                            {t("Receive new stock")}
                          </Text>
                        }
                        value={allocations.get(RECEIVE_SENTINEL_ID) ?? 0}
                      />
                    )}
                  </Flex>
                )}
                {totalAllocated < quantityLeftToPick && (
                  <Text fontSize="xs" textColor="warning">
                    {t("{{count}} item still needs a source", {
                      count: quantityLeftToPick - totalAllocated,
                    })}
                  </Text>
                )}
                {(() => {
                  const hiddenBins = availableBinOptions.filter(
                    (bin) => !visibleBinIds.has(bin.inventoryLevelId),
                  );
                  if (hiddenBins.length === 0) {
                    return null;
                  }
                  const options: RedoListItem<string>[] = hiddenBins.map(
                    (bin) => {
                      const binTypeLabel =
                        bin.binType === StorageLocationType.RECEIVING
                          ? `${t("Receiving")} · `
                          : bin.binType === StorageLocationType.STORAGE
                            ? `${t("Storage")} · `
                            : "";
                      const supportingText =
                        binTypeLabel +
                        [
                          `${bin.availableQuantity} available`,
                          getProblemPickInventoryIdentity(bin.inventoryItemId),
                        ]
                          .filter(Boolean)
                          .join(" · ");
                      const lotText = bin.lotNumber
                        ? ` · ${bin.lotNumber}${
                            bin.expirationDate
                              ? ` · Exp ${bin.expirationDate.toISOString().slice(0, 10)}`
                              : ""
                          }`
                        : "";
                      return {
                        id: bin.inventoryLevelId,
                        value: bin.inventoryLevelId,
                        type: "text" as const,
                        text: `${bin.binName}${lotText}`,
                        supportingText,
                      };
                    },
                  );
                  return (
                    <RedoSingleSelectDropdownInput
                      options={options}
                      optionSelected={({ item: selected }) => {
                        showBin(selected.value);
                      }}
                      placeholder={
                        visibleBins.length === 0
                          ? t("Add a bin…")
                          : t("Add another bin…")
                      }
                      size="sm"
                    />
                  );
                })()}
                {!visibleBinIds.has(RECEIVE_SENTINEL_ID) &&
                  totalAllocated < quantityLeftToPick && (
                    <Flex justify="flex-start">
                      <RedoButton
                        hierarchy="tertiary"
                        onClick={() => showBin(RECEIVE_SENTINEL_ID)}
                        size="sm"
                        text={t("Not in a bin? Receive as new stock")}
                      />
                    </Flex>
                  )}
              </>
            )}
          </Flex>
        ) : (
          <QuantityIncrementDecrement
            compact={isMobile}
            max={quantityLeftToPick}
            min={0}
            setValue={setSelectedQuantity}
            subtitle={t("Remaining: {{count}}", {
              count: quantityLeftToPick - selectedQuantity,
            })}
            title={t("Quantity")}
            value={selectedQuantity}
          />
        )}

        <Flex
          dir={requireSourceBin && !isMobile ? "rowReverse" : "column"}
          gap={isMobile ? "sm" : "md"}
          pt={requireSourceBin ? "md" : undefined}
          w="full"
        >
          <RedoButton
            className="w-full"
            disabled={confirmDisabled}
            hierarchy="primary"
            onClick={() => {
              if (requireSourceBin) {
                onConfirm(totalAllocated, buildSources());
                return;
              }
              onConfirm(selectedQuantity);
            }}
            pending={onConfirmLoad.pending}
            size={isMobile ? "md" : "xl"}
            style={
              !confirmDisabled
                ? { backgroundColor: "#f54a00", outlineColor: "#f54a00" }
                : undefined
            }
            text={t("Confirm")}
            theme="normal"
          />
          <RedoButton
            className="w-full"
            hierarchy="secondary"
            onClick={onClose}
            size={isMobile ? "md" : "xl"}
            text={t("Cancel")}
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
